"use client";

// Components for page design
import "react-datepicker/dist/react-datepicker.css";
import "react-toastify/dist/ReactToastify.css";
import "@/styles/spinner.css";

// Components for page logic
import React, { useEffect, useRef, useMemo } from "react";
import { useState } from "react";
import convertToCSV from "../components/converter";

// Components for page body
import { FaShuffle } from "react-icons/fa6";
import { FaCheckCircle, FaCheck, FaTimes } from "react-icons/fa";
import DatePicker from "react-datepicker";
import { mtmtItems } from "../types/types";
import { toast } from 'react-toastify';

const Main = () => {
  const [year, setYear] = useState(new Date(new Date().setFullYear(new Date().getFullYear() - 1)));
  const [mtmt, setMtmt] = useState({ id: "", isValid: false });
  const [loading, setLoading] = useState(false);
  const [showCheckMark, setShowCheckMark] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [conversionStatus, setConversionStatus] = useState<string>('');

  const filteredItems = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return mtmtItems.filter(
      item =>
        item.id.toLowerCase().includes(term) ||
        item.name.toLowerCase().includes(term) ||
        item.shortName.toLowerCase().includes(term)
    );
  }, [searchTerm]);

  // Create refs for CSSTransition components
  const step1Ref = useRef(null);
  const step2Ref = useRef(null);

  const handleConvert = async () => {
    setLoading(true);
    setConversionStatus(''); // Reset status at start
    try {
      await convertToCSV(
        mtmt.id, 
        year.getFullYear(),
        (update) => {
          if (update.status === 'error') {
            toast.error(update.message);
          } else if (update.status === 'warning') {
            toast.warning(update.message);
          } else if (update.status === 'complete') {
            toast.success(update.message, { autoClose: 1000 });
          }
          setConversionStatus(update.message);
        }
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred during conversion';
      toast.error(errorMessage);
      setConversionStatus(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mtmt.id && year) {
      const url = `https://m2.mtmt.hu/api/publication?cond=published;eq;true&cond=core;eq;true&cond=institutes;inia;${
        mtmt.id
      }&cond=publishedYear;eq;${year
        .getFullYear()
        .toString()}&sort=publishedYear,desc&fields=title&format=json`;
      fetch(url)
        .then((response) => {
          return response.json();
        })
        .then((json) => {
          if (json.status == "422" || json.error) {
            setMtmt((prevState) => ({ ...prevState, isValid: false }));
          } else {
            setMtmt((prevState) => ({ ...prevState, isValid: true }));
          }
        })
        .catch((error) => {});
    }
  }, [mtmt.id]);

  return (
    <div className="flex flex-col items-center justify-center space-y-8 mx-auto mb-20 max-w-4xl">
      <div className="flex flex-col md:flex-row items-center justify-center md:space-x-6 space-y-6 md:space-y-0 mx-auto mt-20">
        <div
          ref={step1Ref}
          className="relative bg-white/70 rounded-lg shadow-md p-4 transition-all duration-300 hover:shadow-lg"
        >
          <p className="text-sm font-semibold text-gray-700 mb-2">
            1. lépés: Év kiválasztása
          </p>
          <DatePicker
            id="year-select"
            className={`mt-2 px-4 py-3 border border-gray-300 rounded-md ${
              year.getFullYear() != new Date().getFullYear()
                ? "text-black"
                : "text-gray-500"
            } bg-white/20 focus:outline-none focus:ring-2 focus:ring-bme-main focus:border-transparent hover:border-bme-main transition-all duration-300`}
            selected={year}
            onChange={(date) => {
              if (date) {
                setYear(date);
              }
            }}
            dateFormat="yyyy"
            showYearPicker
            yearItemNumber={9}
            scrollableYearDropdown
            yearDropdownItemNumber={15}
            onFocus={(event) => event.target.blur()}
          />
          <span className="text-red-500 absolute top-2 right-2 text-lg">
            *
          </span>
        </div>

        <div
          ref={step2Ref}
          className="relative bg-white/70 rounded-lg shadow-md p-4 px-6 transition-all duration-300 hover:shadow-lg"
        >
          {showCheckMark && (
            <div className="flex justify-center">
              <FaCheckCircle className="text-green-500 absolute -top-4 left-1/2 transform -translate-x-1/2 text-4xl animate-bounce" />
            </div>
          )}
          <p className="text-sm font-semibold text-gray-700 mb-2">
            2. lépés: MTMT azonosító megadása
          </p>
          <div className="relative">
            <input
              type="text"
              placeholder="MTMT azonosító vagy név keresése"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowDropdown(true);
                setMtmt(prev => ({ ...prev, id: e.target.value }));
              }}
              onFocus={() => setShowDropdown(true)}
              className={`mt-2 px-4 py-3 border border-gray-300 rounded-md ${
                mtmt.id ? "text-black" : "text-gray-500"
              } bg-white/20 focus:outline-none focus:ring-2 focus:ring-bme-main focus:border-transparent hover:border-bme-main transition-all duration-300 min-w-[500px]`}
            />
            {mtmt.isValid ? (
              <FaCheck className="absolute right-3 bottom-4 text-green-500" />
            ) : (
              <FaTimes className="absolute right-3 bottom-4 text-red-500" />
            )}
            
            {showDropdown && filteredItems.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {filteredItems.map((item) => (
                  <div
                    key={item.id}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => {
                      setMtmt(prev => ({ ...prev, id: item.id }));
                      setSearchTerm(`${item.shortName} - ${item.name}`);
                      setShowDropdown(false);
                    }}
                  >
                    <div className="font-medium">{item.shortName}</div>
                    <div className="text-sm text-gray-600">{item.name}</div>
                    <div className="text-xs text-gray-400">{item.id}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <span className="text-red-500 absolute top-2 right-2 text-lg">*</span>
        </div>
      </div>

      {mtmt.isValid && mtmt.id && (
        <div className="text-sm text-gray-600 mb-20">
          <a
            href={`https://m2.mtmt.hu/api/publication?cond=published;eq;true&cond=core;eq;true&cond=institutes;inia;${mtmt.id}&cond=publishedYear;in;${year.getFullYear()}&sort=publishedYear,desc&sort=firstAuthor,asc&size=5000&labelLang=hun&cite_type=2&incFields=authorships,identifiers,journal,fullPublication,published,book,publishedYear,foreignEdition,foreignLanguage,title,pageLength,firstPage,lastPage,ratings,ratingsForSort,category,subType,type&format=json`}
            target="_blank"
            rel="noopener noreferrer" 
            className="text-bme-main hover:text-bme-dark transition-colors duration-200"
          >
            MTMT API adatok megtekintése
          </a>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center space-y-6">
          <div className="relative flex items-center justify-center">
            <div className="w-24 h-24 relative">
              {/* Outer geometric shapes */}
              <div className="absolute inset-0">
                <div className="absolute inset-0 border-4 border-bme-main rounded-lg animate-[morph_4s_ease-in-out_infinite]"></div>
                <div className="absolute inset-2 border-4 border-blue-500 rotate-45 rounded-lg animate-[morph_4s_ease-in-out_infinite_0.5s]"></div>
                <div className="absolute inset-4 border-4 border-purple-500 rotate-90 rounded-lg animate-[morph_4s_ease-in-out_infinite_1s]"></div>
              </div>

              {/* Dynamic wave effect */}
              <div className="absolute inset-0 flex items-center justify-center">
                {[...Array(3)].map((_, i) => (
                  <div 
                    key={i}
                    className="absolute w-full h-full border-2 border-blue-400/30 rounded-full"
                    style={{
                      animation: `wave 2s ease-out infinite ${i * 0.3}s`,
                      filter: 'blur(1px)'
                    }}
                  ></div>
                ))}
              </div>

              {/* Floating elements */}
              <div className="absolute inset-0">
                <div className="absolute w-2 h-8 bg-gradient-to-b from-blue-500 to-transparent rounded-full animate-[float_2s_ease-in-out_infinite]" 
                     style={{left: '20%', top: '10%'}}></div>
                <div className="absolute w-2 h-6 bg-gradient-to-b from-purple-500 to-transparent rounded-full animate-[float_2s_ease-in-out_infinite_0.7s]"
                     style={{right: '20%', top: '15%'}}></div>
                <div className="absolute w-2 h-7 bg-gradient-to-b from-pink-500 to-transparent rounded-full animate-[float_2s_ease-in-out_infinite_1.4s]"
                     style={{left: '30%', bottom: '10%'}}></div>
              </div>

              {/* Center piece */}
              <div className="absolute inset-6 bg-gradient-to-br from-white to-gray-100 rounded-xl shadow-lg flex items-center justify-center transform animate-[pulse_2s_ease-in-out_infinite]">
                <span className="text-bme-main font-bold text-lg tracking-wider">BME</span>
              </div>

              {/* Energy particles */}
              <div className="absolute inset-0">
                {[...Array(8)].map((_, i) => (
                  <div 
                    key={i}
                    className="absolute w-1 h-1 bg-blue-400 rounded-full"
                    style={{
                      animation: `particle 1.5s ease-out infinite ${i * 0.2}s`,
                      left: `${50 + Math.cos(i * 45 * Math.PI / 180) * 40}%`,
                      top: `${50 + Math.sin(i * 45 * Math.PI / 180) * 40}%`
                    }}
                  ></div>
                ))}
              </div>
            </div>
          </div>
          {conversionStatus && (
            <div className="bg-white/90 backdrop-blur-md px-6 py-2 rounded-2xl shadow-xl border border-gray-200/50 transition-all duration-500 hover:shadow-2xl hover:scale-105 max-w-md">
              <div className="flex items-center justify-center gap-2">
                <p className="text-gray-800 text-center font-semibold text-base">
                  {conversionStatus}
                </p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <button
          className={`group relative w-[180px] sm:w-[200px] px-8 sm:px-10 py-3 sm:py-4 rounded-xl ${
            mtmt.isValid
              ? "bg-gradient-to-br from-bme-main to-bme-main-hover text-white"
              : "bg-gray-200 cursor-not-allowed"
          } focus:outline-none active:scale-95 transition-all duration-300 text-sm sm:text-base shadow-md hover:shadow-xl flex items-center justify-center font-bold overflow-hidden`}
          disabled={!mtmt.isValid}
          onClick={handleConvert}
        >
          <div className="relative flex items-center justify-center">
            <FaShuffle className="absolute text-lg transition-all duration-300 left-0 group-hover:left-1/2 group-hover:-translate-x-1/2 group-hover:scale-125" />
            <span className="ml-6 tracking-wider transition-all duration-300 group-hover:translate-x-[150%] group-hover:opacity-0">
              Konvertálás
            </span>
          </div>
        </button>
      )}
    </div>
  );
};

export default Main;
